import { useState, useEffect, useCallback } from 'react';
import {
    Grid, Card, CardHeader, List, ListItemButton,
    ListItemText, ListItemIcon, Checkbox, Button, Divider
} from '@mui/material';

const not = (a, b) => {
    const bIds = b.map((item) => item.id);
    return a.filter((item) => !bIds.includes(item.id));
};

const intersection = (a, b) => {
    const bIds = b.map((item) => item.id);
    return a.filter((item) => bIds.includes(item.id));
};

const union = (a, b) => {
    const aIds = a.map(item => item.id);
    const newItems = b.filter(item => !aIds.includes(item.id));
    return [...a, ...newItems];
};

const ListTransfer = ({
    loading,
    allItems,
    selectedItems,
    onSelectionChange,
    textLeft,
    textRight,
    textSelected
}) => {

    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, selectedItems);

    // Actualizar left cuando cambian allItems o selectedItems
    useEffect(() => {
        if (!Array.isArray(allItems)) {
            setLeft([]);
            return;
        }

        const selectedIds = selectedItems.map(item => item.id).filter(id => id !== undefined);
        const newLeft = allItems.filter(item => !selectedIds.includes(item.id));
        setLeft(newLeft);
    }, [allItems, selectedItems]);

    const handleToggle = useCallback((value) => () => {
        const currentIndex = checked.findIndex(item => item.id === value.id);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    }, [checked]);

    const numberOfChecked = useCallback((items) => {
        if (!Array.isArray(items)) return 0;
        return intersection(checked, items).length;
    }, [checked]);

    const handleToggleAll = useCallback((items) => () => {
        if (!Array.isArray(items)) return;

        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    }, [checked, numberOfChecked]);

    const handleCheckedRight = useCallback(() => {
        if (leftChecked.length === 0) return;

        const newSelected = [...selectedItems, ...leftChecked];
        onSelectionChange(newSelected);
        setChecked(not(checked, leftChecked));
    }, [selectedItems, leftChecked, checked, onSelectionChange]);

    const handleCheckedLeft = useCallback(() => {
        if (rightChecked.length === 0) return;

        const newSelected = not(selectedItems, rightChecked);
        onSelectionChange(newSelected);
        setChecked(not(checked, rightChecked));
    }, [selectedItems, rightChecked, checked, onSelectionChange]);

    const customList = (title, items) => {
        const sortedItems = [...items].sort((a, b) => {
            const labelA = (a.name || a.title || '').toString();
            const labelB = (b.name || b.title || '').toString();
            return labelA.localeCompare(labelB, 'es', { sensitivity: 'base' });
        });

        return (
            <Card sx={{ boxShadow: 1 }}>
                <CardHeader
                    sx={{ px: 2, py: 1 }}
                    avatar={
                        <Checkbox
                            onClick={handleToggleAll(items)}
                            checked={
                                numberOfChecked(items) === items.length &&
                                items.length !== 0
                            }
                            indeterminate={
                                numberOfChecked(items) !== items.length &&
                                numberOfChecked(items) !== 0
                            }
                            disabled={loading || items.length === 0}
                        />
                    }
                    title={title}
                    subheader={`${numberOfChecked(items)}/${items.length} `}
                />

                <Divider />

                <List
                    sx={{
                        width: "100%",
                        height: 300,
                        bgcolor: "background.paper",
                        overflow: "auto",
                    }}
                    dense
                    component="div"
                    role="list"
                >
                    {sortedItems.map((value, index) => {
                        const itemKey = value.id || `item-${index}`;
                        const labelId = `transfer-list-item-${itemKey}-label`;

                        return (
                            <ListItemButton
                                key={itemKey}
                                role="listitem"
                                onClick={handleToggle(value)}
                            >
                                <ListItemIcon>
                                    <Checkbox
                                        checked={checked.some((i) => i.id === value.id)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>

                                <ListItemText
                                    id={labelId}
                                    primary={value.name || value.title || `Item ${index + 1}`}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Card>
        );
    };

    return (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>

                {customList(textLeft, left)}
            </Grid>

            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                <Grid container direction="column" alignItems="center">
                    <Button
                        sx={{ my: 0.5 }}
                        variant="contained"
                        size="small"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected right"
                    >
                        &gt;
                    </Button>

                    <Button
                        sx={{ my: 0.5 }}
                        variant="contained"
                        size="small"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected left"
                    >
                        &lt;
                    </Button>
                </Grid>
            </Grid>

            <Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>
                {customList(textRight, selectedItems)}
            </Grid>
        </Grid>
    );
};

export default ListTransfer;